// Tileable noise volumes for the volumetric clouds, generated once on the GPU.
//
// The recipes are TAKEN's (src/world/cloudNoiseData.ts, cloudWeatherData.ts),
// baked here in 3D instead of 2D + 3D: this field is read on a shell around a
// planet, so a flat weather map would only work near one launch site. Three
// textures, each rendered slice by slice into a WebGL3DRenderTarget the first
// time rocketry mode needs clouds, then only sampled:
//
//   weather  128^3 RGBA8, an 11 km tile. R is the placement field (three
//            octaves of gradient noise read through its own domain warp,
//            stretched so the coverage threshold means the same thing it
//            does in TAKEN), G the macro front that clusters masses and
//            decides how deep they grow, B/A the two warp fields the march
//            de-tiles the shape and erosion lookups with. Read on a shell at
//            cloud-base height, so a cloud keeps one footprint base to top.
//   shape    128^3 RGBA8, a 574 m tile. R is Perlin-Worley (tiling gradient
//            Perlin dilated by a Worley FBM: rounded cores with connective
//            tissue), G/B/A Worley FBMs at rising frequency. The coverage
//            carves the cloud out of these.
//   detail   64^3 RGBA8, a 176 m tile. The Nubis3 up-rez basis: R/G curly
//            Alligator (wisps) at two frequencies, B/A Alligator (billows)
//            at two frequencies. It erodes edges, never adds density.
//
// Every noise is periodic in the texture's own tile, so the fields repeat
// seamlessly when sampled with RepeatWrapping in world space. Hashes are
// integer PCG, so the result is identical on every GPU. The shape and detail
// volumes carry mipmaps (built by hand: three does not generate them for 3D
// render targets), because the march picks a level from the pixel footprint.

import * as THREE from 'three';

const VERT = /* glsl */`
in vec3 position;
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const COMMON = /* glsl */`
precision highp float;
precision highp int;
uniform float uSlice;
uniform float uSize;
uniform vec2 uFit;          // placement stretch: subtract, then scale
uniform vec4 uFit2;         // the orbit volume's R and G stretch: (subtract, scale) x 2
out vec4 oColor;

uvec3 pcg3d(uvec3 v) {
  v = v * 1664525u + 1013904223u;
  v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
  v ^= v >> 16u;
  v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
  return v;
}
// Random vector in [0,1)^3 for integer lattice cell c, wrapped to the period.
vec3 hash3(ivec3 c, int period, uint seed) {
  ivec3 w = ((c % period) + period) % period;
  uvec3 h = pcg3d(uvec3(w) + uvec3(seed, seed * 7u + 3u, seed * 13u + 5u));
  return vec3(h) * (1.0 / 4294967296.0);
}

float perlin(vec3 p, int P, uint seed) {
  vec3 i = floor(p);
  vec3 f = p - i;
  ivec3 c = ivec3(i);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float n[8];
  for (int k = 0; k < 8; k++) {
    ivec3 o = ivec3(k & 1, (k >> 1) & 1, (k >> 2) & 1);
    vec3 g = normalize(hash3(c + o, P, seed) * 2.0 - 1.0 + 1e-5);
    n[k] = dot(g, f - vec3(o));
  }
  return mix(mix(mix(n[0], n[1], u.x), mix(n[2], n[3], u.x), u.y),
             mix(mix(n[4], n[5], u.x), mix(n[6], n[7], u.x), u.y), u.z);
}

// One gradient-noise octave at f cells per tile, in [0,1] (TAKEN's mapping:
// unit gradients keep raw Perlin inside about +-0.7, and 0.9 spreads it).
float pn01(vec3 q, int f, uint seed) {
  return clamp(0.5 + perlin(q * float(f), f, seed) * 0.9, 0.0, 1.0);
}

// Distance to the nearest feature point, one point per cell.
float worley(vec3 p, int P, uint seed) {
  vec3 i = floor(p);
  vec3 f = p - i;
  ivec3 c = ivec3(i);
  float d = 8.0;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int x = -1; x <= 1; x++) {
    ivec3 o = ivec3(x, y, z);
    vec3 r = vec3(o) + hash3(c + o, P, seed) - f;
    d = min(d, dot(r, r));
  }
  return sqrt(d);
}

// The nearest and second-nearest feature points: F2 - F1 is the Voronoi
// lane, zero on a cell boundary and largest at the cell's centre.
vec2 worley2(vec3 p, int P, uint seed) {
  vec3 i = floor(p);
  vec3 f = p - i;
  ivec3 c = ivec3(i);
  float d1 = 8.0, d2 = 8.0;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int x = -1; x <= 1; x++) {
    ivec3 o = ivec3(x, y, z);
    vec3 r = vec3(o) + hash3(c + o, P, seed) - f;
    float d = dot(r, r);
    if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
  }
  return sqrt(vec2(d1, d2));
}

float remapc(float v, float a, float b) { return clamp((v - a) / (b - a), 0.0, 1.0); }
float remap(float v, float il, float ih, float ol, float oh) {
  return ol + (clamp(v, il, ih) - il) * (oh - ol) / max(ih - il, 1e-5);
}

vec3 texelCoord() { return vec3(gl_FragCoord.xy, uSlice + 0.5) / uSize; }
`;

// ---------------------------------------------------------------------------
// Shape volume: the Nubis/HZD Perlin-Worley recipe.
// ---------------------------------------------------------------------------
const SHAPE_FRAG = COMMON + /* glsl */`
// Anchor the gradient-Perlin result to the value-noise distribution the
// coverage carve was tuned against (TAKEN's matchShapeDistribution).
float matchShape(float v) {
  if (v < 0.5922) return remap(v, 0.0, 0.5922, 0.0, 0.5569);
  if (v < 0.7451) return remap(v, 0.5922, 0.7451, 0.5569, 0.7294);
  if (v < 0.8627) return remap(v, 0.7451, 0.8627, 0.7294, 0.8549);
  return remap(v, 0.8627, 1.0, 0.8549, 1.0);
}

void main() {
  vec3 q = texelCoord();
  float iw4 = 1.0 - worley(q * 4.0, 4, 41u);
  float iw8 = 1.0 - worley(q * 8.0, 8, 43u);
  float iw16 = 1.0 - worley(q * 16.0, 16, 47u);
  float iw32 = 1.0 - worley(q * 32.0, 32, 53u);
  float wfbm4 = iw4 * 0.625 + iw8 * 0.25 + iw16 * 0.125;
  float wfbm8 = iw8 * 0.625 + iw16 * 0.25 + iw32 * 0.125;
  float wfbm16 = iw16 * 0.75 + iw32 * 0.25;
  float pf = (pn01(q, 4, 101u) * 0.5 + pn01(q, 8, 211u) * 0.25
            + pn01(q, 16, 307u) * 0.125 + pn01(q, 32, 401u) * 0.0625) / 0.9375;
  // Perlin-Worley: the Perlin mass dilated by the Worley FBM.
  oColor = vec4(matchShape(remap(pf, 0.0, 1.0, wfbm4, 1.0)), wfbm4, wfbm8, wfbm16);
}
`;

// ---------------------------------------------------------------------------
// Detail volume: the Nubis3 up-rez basis (Alligator and curly Alligator).
// ---------------------------------------------------------------------------
const DETAIL_FRAG = COMMON + /* glsl */`
// ALLIGATOR noise (SideFX, the Nubis3 billow basis), not Worley: each cell's
// feature point carries its own amplitude, splatted through a smooth radial
// basis, and the value is the largest contribution minus the second largest.
// Random amplitudes break Worley's uniform cell radius, and the difference
// puts the crease where two lobes of unequal strength meet, which is the
// cauliflower of a growing cumulus rather than packed spheres.
float alligator(vec3 p, int P, uint seed) {
  vec3 i = floor(p);
  vec3 f = p - i;
  ivec3 c = ivec3(i);
  float m1 = 0.0, m2 = 0.0;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int x = -1; x <= 1; x++) {
    ivec3 o = ivec3(x, y, z);
    vec3 r = vec3(o) + hash3(c + o, P, seed) - f;
    float d = length(r);
    if (d >= 1.0) continue;
    // Held off zero so a cell never drops out and leaves a dead void.
    float amp = 0.35 + 0.65 * hash3(c + o, P, seed + 61u).x;
    float t = 1.0 - d;
    float v = amp * t * t * (3.0 - 2.0 * t);
    if (v > m1) { m2 = m1; m1 = v; } else if (v > m2) { m2 = v; }
  }
  return min((m1 - m2) * 1.35, 1.0);
}

// Divergence-free curl of three gradient-noise potentials: it swirls the
// domain instead of compressing it, so the noise it distorts does not smear
// into streaks. Bake time only.
vec3 curl3(vec3 q, int P, uint seed) {
  const float e = 0.05;
  vec3 ex = vec3(e, 0.0, 0.0), ey = vec3(0.0, e, 0.0), ez = vec3(0.0, 0.0, e);
  vec3 p = q * float(P);
  float dady = perlin(p + ey, P, seed) - perlin(p - ey, P, seed);
  float dadz = perlin(p + ez, P, seed) - perlin(p - ez, P, seed);
  float dbdx = perlin(p + ex, P, seed + 137u) - perlin(p - ex, P, seed + 137u);
  float dbdz = perlin(p + ez, P, seed + 137u) - perlin(p - ez, P, seed + 137u);
  float dcdx = perlin(p + ex, P, seed + 263u) - perlin(p - ex, P, seed + 263u);
  float dcdy = perlin(p + ey, P, seed + 263u) - perlin(p - ey, P, seed + 263u);
  return vec3(dcdy - dbdz, dadz - dcdx, dbdx - dady) / (2.0 * e);
}

// The Nubis3 wisp basis: an inverted Alligator (webs rather than lumps)
// pushed through the curl field, which draws the webs out into filaments.
float curlyAlligator(vec3 q, int P, uint seed, int curlP, float amount) {
  vec3 cv = curl3(q, curlP, seed + 911u);
  return 1.0 - alligator(q * float(P) + cv * amount, P, seed);
}

void main() {
  vec3 q = texelCoord();
  oColor = vec4(
    curlyAlligator(q, 6, 5u, 3, 0.55),
    curlyAlligator(q, 16, 11u, 6, 0.28),
    alligator(q * 4.0, 4, 3u),
    alligator(q * 12.0, 12, 9u));
}
`;

// ---------------------------------------------------------------------------
// Weather volume: TAKEN's placement / macro front / domain warp, in 3D.
// ---------------------------------------------------------------------------
const WEATHER_FRAG = COMMON + /* glsl */`
void main() {
  vec3 q = texelCoord();
  // Two-scale domain warp. B and A go out for the march to de-tile its shape
  // and erosion lookups with; a third component only bends the placement.
  float wu = (pn01(q, 8, 601u) * 820.0 + pn01(q, 23, 701u) * 240.0) / 1060.0;
  float wv = (pn01(q, 8, 809u) * 820.0 + pn01(q, 23, 907u) * 240.0) / 1060.0;
  float ww = (pn01(q, 8, 1013u) * 820.0 + pn01(q, 23, 1109u) * 240.0) / 1060.0;
  // The placement is read through that warp (TAKEN bends the placement domain
  // before any wind alignment: straight rows read as corduroy).
  vec3 qw = q + (vec3(wu, wv, ww) - 0.5) * 0.0327;
  // Three octaves from 18 cells in the 14 km tile: masses about 780 m across,
  // TAKEN's are 760 m, seen from the ground; from tens of kilometres up that
  // TAKEN's scale (758 m) against a 900 m slab: one tower per mass. Wider
  // masses spanned several crown cells and read as stepped loaves; the
  // count is held down by coverage instead, and from high up the satellite
  // formations take over. The constant is the mean of the octave TAKEN drops above its
  // map's Nyquist, kept so the level is unchanged.
  float raw = pn01(qw, 18, 211u) * 0.5 + pn01(qw, 36, 307u) * 0.25
            + pn01(qw, 72, 401u) * 0.125 + 0.03125;
  // Stretch the narrow band the FBM actually occupies to 0..1, so the
  // coverage threshold that trims it means a fraction of sky. uFit is
  // measured from the field itself (see fitPlacement).
  float placement = clamp((raw - uFit.x) * uFit.y, 0.0, 1.0);
  // Macro weather at 4 cells (3.5 km): it clusters the masses into fronts
  // with clear lanes, and decides how deep the clouds under it grow.
  float macro = pn01(q, 4, 503u);
  oColor = vec4(placement, macro, wu, wv);
}
`;

// ---------------------------------------------------------------------------
// Orbit volume: one band of the cloud tops' latent field, the structure a
// cloud field has under the cloud map's texel (src/shaders/earthcloudtop.js
// reads it at three scales a factor of three apart and sums the reads, so
// this texture is the unit of that spectrum). Read on the sphere in metres.
//   R  the band, Gaussianised: a Perlin-Worley (gradient octaves at 4, 8
//      and 16 cells a tile dilated by an inverted Worley at 8 and 16), so a
//      high threshold cuts rounded cauliflower cells, pushed through its own
//      measured distribution onto a unit normal so that sums of bands stay
//      normal and a threshold on them has a closed-form mean (the carve's
//      prefilter depends on it). Stored as 0.5 + z / 8.
//   GBA  the gradient of that normal value along the tile's x, y and z, in
//      units per tile, stored as 0.5 + g / ORBIT_GRAD_RANGE. The carve takes
//      the relief normal from these, so shading needs no second evaluation,
//      and a mip of the gradient is the gradient of the mip.
// uRaw 1 writes the raw Perlin-Worley instead (as two bytes), for the probe
// that measures its distribution; uQ is that distribution's value at 29
// normal quantiles (z from -3.5 to 3.5 in quarter steps).
// ---------------------------------------------------------------------------
export const ORBIT_GRAD_RANGE = 256.0;
const ORBIT_FRAG = COMMON + /* glsl */`
uniform float uRaw;
uniform float uQ[29];
float orbitRaw(vec3 q) {
  vec3 wq = q + (vec3(pn01(q, 4, 2201u), pn01(q, 4, 2203u), pn01(q, 4, 2207u)) - 0.5) * 0.04;
  float pf = pn01(wq, 4, 2221u) * 0.30 + pn01(wq, 8, 2223u) * 0.45 + pn01(wq, 16, 2227u) * 0.25;
  float wf = (1.0 - worley(wq * 8.0, 8, 2211u)) * 0.75 + (1.0 - worley(wq * 16.0, 16, 2213u)) * 0.25;
  return remap(pf, 0.0, 1.0, wf, 1.0);
}
// The raw value's place in its own distribution, as a unit normal.
float orbitNormal(float r) {
  if (r <= uQ[0]) return -3.5 - (uQ[0] - r) / max(uQ[1] - uQ[0], 1e-5) * 0.25;
  for (int i = 0; i < 28; i++) {
    if (r < uQ[i + 1]) return -3.5 + (float(i) + (r - uQ[i]) / max(uQ[i + 1] - uQ[i], 1e-6)) * 0.25;
  }
  return 3.5 + (r - uQ[28]) / max(uQ[28] - uQ[27], 1e-5) * 0.25;
}
void main() {
  vec3 q = texelCoord();
  if (uRaw > 0.5) {
    // Two bytes of the raw value (high, then the remainder), so the probe
    // reads 16 bits through a plain RGBA8 target on every device.
    float r = clamp(orbitRaw(q), 0.0, 1.0) * 255.0;
    float hi = min(floor(r), 254.0);
    oColor = vec4(hi / 255.0, clamp(r - hi, 0.0, 1.0), 0.0, 1.0);
    return;
  }
  float z = clamp(orbitNormal(orbitRaw(q)), -3.99, 3.99);
  // A quarter texel either side: the gradient of what the texel holds.
  float h = 0.25 / uSize;
  vec3 g = vec3(
    orbitNormal(orbitRaw(q + vec3(h, 0.0, 0.0))) - orbitNormal(orbitRaw(q - vec3(h, 0.0, 0.0))),
    orbitNormal(orbitRaw(q + vec3(0.0, h, 0.0))) - orbitNormal(orbitRaw(q - vec3(0.0, h, 0.0))),
    orbitNormal(orbitRaw(q + vec3(0.0, 0.0, h))) - orbitNormal(orbitRaw(q - vec3(0.0, 0.0, h)))) / (2.0 * h);
  oColor = vec4(0.5 + z / 8.0, clamp(0.5 + g / ${ORBIT_GRAD_RANGE.toFixed(1)}, 0.0, 1.0));
}
`;

// The orbit band's raw distribution: a probe of the raw Perlin-Worley, read
// back sorted, and returned as its value at the 29 normal quantiles the bake
// maps it through (the same field on a coarser grid, so the same
// distribution). The probe is an RGBA8 target read as bytes, the one
// combination WebGL2 guarantees: a float target needs EXT_color_buffer_float,
// and without it the read-back came out all zeros, the quantiles collapsed
// and every texel of the map with any cloud in it carved to solid cloud.
function fitOrbitQuantiles(renderer) {
  const size = 48;
  const rt = bake(renderer, size, ORBIT_FRAG, { extra: { uRaw: { value: 1 }, uQ: { value: new Float32Array(29) } } });
  const gl = renderer.getContext();
  const prevTarget = renderer.getRenderTarget();
  const buf = new Uint8Array(size * size * 4);
  const v = new Float32Array(size * size * size);
  for (let z = 0; z < size; z++) {
    renderer.setRenderTarget(rt, z);
    gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, buf);
    for (let i = 0; i < size * size; i++) v[z * size * size + i] = (buf[i * 4] + buf[i * 4 + 1] / 255) / 255;
  }
  renderer.setRenderTarget(prevTarget);
  rt.dispose();
  v.sort();
  const q = new Float32Array(29);
  for (let i = 0; i < 29; i++) {
    const p = normalCdf(-3.5 + i * 0.25);
    q[i] = v[Math.min(v.length - 1, Math.max(0, Math.round(p * (v.length - 1))))];
  }
  // Ties in the tails would divide by zero in the bake's inverse.
  for (let i = 1; i < 29; i++) q[i] = Math.max(q[i], q[i - 1] + 1e-5);
  return q;
}
function normalCdf(x) {
  // Abramowitz and Stegun 7.1.26.
  const t = 1 / (1 + 0.3275911 * Math.abs(x) / Math.SQRT2);
  const e = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x / 2);
  return 0.5 * (1 + Math.sign(x) * e);
}

function makeTarget(size, type) {
  const rt = new THREE.WebGL3DRenderTarget(size, size, size, {
    depthBuffer: false,
    stencilBuffer: false
  });
  const tex = rt.texture;
  tex.format = THREE.RGBAFormat;
  tex.type = type;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = tex.wrapR = THREE.RepeatWrapping;
  tex.generateMipmaps = false;
  tex.name = 'cloudNoise' + size;
  return rt;
}

function bake(renderer, size, fragmentShader, { type = THREE.UnsignedByteType, fit = [0, 1], fit2 = [0, 1, 0, 1], mips = false, extra = null } = {}) {
  const rt = makeTarget(size, type);
  const mat = new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: VERT,
    fragmentShader,
    uniforms: { uSlice: { value: 0 }, uSize: { value: size }, uFit: { value: new THREE.Vector2(fit[0], fit[1]) }, uFit2: { value: new THREE.Vector4(...fit2) }, ...(extra || {}) },
    depthTest: false,
    depthWrite: false
  });
  const scene = new THREE.Scene();
  const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  quad.frustumCulled = false;
  scene.add(quad);
  const prevTarget = renderer.getRenderTarget();
  const prevAuto = renderer.autoClear;
  renderer.autoClear = false;
  for (let z = 0; z < size; z++) {
    mat.uniforms.uSlice.value = z;
    renderer.setRenderTarget(rt, z);
    renderer.render(scene, cam);
  }
  renderer.setRenderTarget(prevTarget);
  renderer.autoClear = prevAuto;
  quad.geometry.dispose();
  mat.dispose();
  // Mip levels for the volumes the march reads at a level chosen from the
  // pixel footprint. three only generates mipmaps for 2D render targets (it
  // binds TEXTURE_2D), so do it here on the 3D texture itself; the storage is
  // mutable (texImage3D), so generateMipmap allocates the chain.
  if (mips) {
    const gl = renderer.getContext();
    const props = renderer.properties.get(rt.texture);
    if (props && props.__webglTexture) {
      renderer.state.bindTexture(gl.TEXTURE_3D, props.__webglTexture);
      gl.generateMipmap(gl.TEXTURE_3D);
      gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      renderer.state.unbindTexture();
    }
  }
  return rt;
}

// A noise FBM occupies a narrow band, and where that band sits decides what
// a threshold on it means. Bake a small probe of the field with no stretch,
// read a few slices back, and return the 5th and 95th percentiles of each
// channel asked for as (subtract, scale), so the real bake can spread them
// over 0..1.
function fitField(renderer, frag, channels = [0]) {
  const size = 48;
  const rt = bake(renderer, size, frag, { fit: [0, 1], fit2: [0, 1, 0, 1] });
  const gl = renderer.getContext();
  const prevTarget = renderer.getRenderTarget();
  const buf = new Uint8Array(size * size * 4);
  const values = channels.map(() => []);
  for (const z of [3, 11, 19, 27, 35, 43]) {
    renderer.setRenderTarget(rt, z);
    gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, buf);
    for (let i = 0; i < buf.length; i += 4) channels.forEach((c, k) => values[k].push(buf[i + c] / 255));
  }
  renderer.setRenderTarget(prevTarget);
  rt.dispose();
  const out = [];
  for (const v of values) {
    v.sort((a, b) => a - b);
    const lo = v[Math.floor(v.length * 0.05)];
    const hi = v[Math.floor(v.length * 0.95)];
    out.push(lo, 1 / Math.max(hi - lo, 1e-3));
  }
  return out;
}
function fitPlacement(renderer) { return fitField(renderer, WEATHER_FRAG, [0]); }

// Build the volumes, once per renderer. Every caller gets the same
// volumes (the volumetric march, the orbital cloud layer and the globe's
// cloud tops all read them), and they are freed when the last one disposes
// its handle. Returns { shape, detail, weather, orbit, fit, dispose() }; each field
// is the WebGL3DRenderTarget whose .texture the shaders sample.
const shared = new WeakMap();
export function buildCloudNoise(renderer) {
  let s = shared.get(renderer);
  if (!s) {
    const fit = fitPlacement(renderer);
    const shape = bake(renderer, 128, SHAPE_FRAG, { mips: true });
    const detail = bake(renderer, 64, DETAIL_FRAG, { mips: true });
    const weather = bake(renderer, 128, WEATHER_FRAG, { fit, mips: true });
    const orbit = bake(renderer, 128, ORBIT_FRAG, { mips: true, extra: { uRaw: { value: 0 }, uQ: { value: fitOrbitQuantiles(renderer) } } });
    s = { shape, detail, weather, orbit, fit, users: 0 };
    shared.set(renderer, s);
  }
  s.users++;
  let released = false;
  return {
    shape: s.shape, detail: s.detail, weather: s.weather, orbit: s.orbit, fit: s.fit,
    dispose() {
      if (released) return;
      released = true;
      if (--s.users > 0) return;
      s.shape.dispose();
      s.detail.dispose();
      s.weather.dispose();
      s.orbit.dispose();
      if (shared.get(renderer) === s) shared.delete(renderer);
    }
  };
}

// The volumes if someone has built them, without taking a handle.
export function getCloudNoise(renderer) {
  const s = shared.get(renderer);
  return s ? { shape: s.shape, detail: s.detail, weather: s.weather, orbit: s.orbit, fit: s.fit } : null;
}
